import { z } from "zod";

import type { BinaryOperator, Operator, TernaryOperator } from "../operators.ts";
import type { IValue, PrimitiveValue } from "../values.ts";

// Condition type discriminator values
export const ConditionType = z.enum([
  "number",
  "string",
  "boolean",
  "numberArray",
  "stringArray",
  "booleanArray",
  "objectArray",
]);
export type ConditionType = z.infer<typeof ConditionType>;

export interface ICondition {
  type: string;
  operator: string;
}

export interface IConditionFor<TOperator extends Operator> extends ICondition {
  operator: TOperator;
}

export interface IBinaryCondition<
  TOperator extends Operator & BinaryOperator,
  TValue extends IValue | PrimitiveValue,
> extends IConditionFor<TOperator> {
  subject: TValue;
  object: TValue;
}

export interface ITernaryCondition<
  TOperator extends Operator & TernaryOperator,
  TValue extends IValue | PrimitiveValue,
> extends IConditionFor<TOperator> {
  subject: TValue;
  left: TValue;
  right: TValue;
}

// Zod schema factory functions
export function binaryCondition<
  TType extends z.ZodTypeAny,
  TOperator extends z.ZodTypeAny,
  TValue extends z.ZodTypeAny,
>(typeSchema: TType, operatorSchema: TOperator, valueSchema: TValue) {
  return z.object({
    type: typeSchema,
    operator: operatorSchema,
    subject: valueSchema,
    object: valueSchema,
  });
}

export function ternaryCondition<
  TType extends z.ZodTypeAny,
  TOperator extends z.ZodTypeAny,
  TValue extends z.ZodTypeAny,
>(typeSchema: TType, operatorSchema: TOperator, valueSchema: TValue) {
  return z.object({
    type: typeSchema,
    operator: operatorSchema,
    subject: valueSchema,
    left: valueSchema,
    right: valueSchema,
  });
}
